import type { Location, Reference } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { ExtendedLocation } from '@seen/core/models/location'
import { Button } from '@seen/ui'
import { IconPlus } from '@tabler/icons-react'
import { type ComponentProps, type JSX, useMemo } from 'react'
import {
  Combobox,
  type ComboboxOption,
} from '#src/components/core/combobox/Combobox.tsx'
import { LocationNameplate } from '#src/components/nameplates/LocationNameplate.tsx'
import { LocationOption } from '#src/components/options/LocationOption.tsx'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

export function LocationComboboxButton({
  value,
  onChange,
  onEdit,
  onCreate,
  ...delegated
}: {
  value?: Reference<Location>
  onChange: (value: Reference<Location>) => void
  onEdit?: (location: ExtendedLocation) => void
  onCreate?: () => void
} & Omit<ComponentProps<typeof Button>, 'onChange' | 'value'>): JSX.Element {
  const { data } = useSearchResourcePages(
    ExtendedLocation,
    ExtendedLocation.baseSearchQuery()
  )
  const locations = useMemo(() => data ?? [], [data])

  const options: ComboboxOption<string | null>[] = useMemo(
    () => [
      ...(onCreate
        ? [
            {
              value: 'create',
              label: (
                <div className="flex items-center gap-2">
                  <IconPlus size={14} />
                  Create new
                </div>
              ),
              keywords: ['create', 'new'],
            },
          ]
        : []),
      ...locations.map((l) => ({
        value: l.reference.reference,
        label: (
          <LocationOption
            location={l.reference}
            hideIcon
            onEdit={(e, l) => {
              e.stopPropagation()
              if (l) {
                onEdit?.(l)
              }
            }}
          />
        ),
        keywords: l.searchKeywords,
      })),
    ],
    [locations, onEdit, onCreate]
  )

  return (
    <Combobox
      options={options}
      onSelect={(referenceString) => {
        if (referenceString === 'create') {
          invariant(onCreate, 'onCreate is required')
          onCreate()
          return
        }
        if (!referenceString) {
          return
        }

        const location = locations.find(
          (l) => l.reference.reference === referenceString
        )
        onChange(location?.reference ?? { reference: referenceString })
      }}
    >
      <Button
        variant="outline"
        role="combobox"
        className="w-full justify-between"
        aria-label="Select location"
        {...delegated}
      >
        {value ? <LocationNameplate location={value} /> : 'Search location…'}
      </Button>
    </Combobox>
  )
}
